import type {
  CalibreData,
  CalibreImage,
  CalibreTier,
  CalibreTierOverrides,
} from '../../services/firebase';

/**
 * Shared definition of the calibre-tier overridable fields, used by both the
 * calibre-editor tier form (CalibreTiersEditor) and the ref-editor effective-
 * spec preview. Keeps the whitelist, labels, and lookup mappings in ONE place
 * so the two surfaces never drift.
 *
 * Identity fields (canonical name, manufacturer, baseMovement, movementType)
 * are deliberately absent — they are never overridable at the tier level.
 */
export type OverrideKey = keyof CalibreTierOverrides;

export type OverrideKind =
  | 'boolean'
  | 'number'
  | 'text'
  | 'lookup'
  | 'multi-lookup';

/**
 * Every value an override field can hold. `string[]` is carried by the
 * multi-select override keys (`balanceType`, `decorations`,
 * `caseMaterialsAvailable`), which store lookup slugs.
 */
export type OverrideValue = boolean | number | string | string[] | undefined;

export interface OverridableFieldConfig {
  key: OverrideKey;
  label: string;
  kind: OverrideKind;
  /** For `kind: 'lookup'`, the lookup collection backing the picker. */
  lookupCollection?: string;
  /** Optional placeholder for text/number inputs. */
  placeholder?: string;
}

/**
 * The overridable fields per `CalibreTierOverrides`. Kinds + lookup
 * collections mirror how the same field is edited on the base calibre editor
 * (CustomCalibreModal): `regulatorSystem`/`hairspring`/`escapement` are lookup
 * selects, `rotorType` is free text, the numeric specs are numbers, and the
 * certification/spec flags are booleans.
 */
export const OVERRIDABLE_FIELDS: OverridableFieldConfig[] = [
  { key: 'isChronometer', label: 'Chronometer', kind: 'boolean' },
  { key: 'isCosc', label: 'COSC Certified', kind: 'boolean' },
  { key: 'isGlashuetteRegulation', label: 'Glashütte Regulation', kind: 'boolean' },
  { key: 'isGenevaSeal', label: 'Geneva Seal', kind: 'boolean' },
  { key: 'isGrandSeikoStandard', label: 'Grand Seiko Standard', kind: 'boolean' },
  { key: 'isMETAS', label: 'METAS Certified', kind: 'boolean' },
  { key: 'hacking', label: 'Hacking', kind: 'boolean' },
  { key: 'quickSetDate', label: 'Quick Set Date', kind: 'boolean' },
  // Coarse date PRESENCE, not the date type. The base spec carries the typed
  // `date[]` array; a tier can only say "has one" / "hasn't one" (see
  // baseValueFor, which derives the base side from `date[]` being non-empty).
  { key: 'hasDate', label: 'Has Date', kind: 'boolean' },
  {
    key: 'balanceType',
    label: 'Balance Type',
    kind: 'multi-lookup',
    lookupCollection: 'lookup_balance_type',
  },
  {
    key: 'decorations',
    label: 'Decorations',
    kind: 'multi-lookup',
    lookupCollection: 'lookup_movement_decoration',
  },
  // Case AVAILABILITY, not composition — which case materials this tier is sold
  // in. Backed by the same collection as the reference-level case material,
  // whose config is `isMultiSelect: false` for that single-select field; the
  // multi-ness here comes from this `multi-lookup` kind, not the config flag.
  {
    key: 'caseMaterialsAvailable',
    label: 'Case Materials Available',
    kind: 'multi-lookup',
    lookupCollection: 'lookup_case_materials',
  },
  {
    key: 'rotorMaterial',
    label: 'Rotor Material',
    kind: 'lookup',
    lookupCollection: 'lookup_rotor_material',
  },
  {
    key: 'bridgeMaterial',
    label: 'Bridge Material',
    kind: 'lookup',
    lookupCollection: 'lookup_bridge_material',
  },
  // The mainplate's PRIMARY surface treatment. Distinct from the base
  // `movementFinishing` (overall finishing GRADE, not tier-overridable) and
  // from `decorations` (every technique present, unranked).
  {
    key: 'plateFinishing',
    label: 'Plate Finishing',
    kind: 'lookup',
    lookupCollection: 'lookup_plate_finishing',
  },
  {
    key: 'regulatorSystem',
    label: 'Regulator System',
    kind: 'lookup',
    lookupCollection: 'lookup_regulation',
  },
  {
    key: 'hairspring',
    label: 'Hairspring',
    kind: 'lookup',
    lookupCollection: 'lookup_balance_spring',
  },
  {
    key: 'escapement',
    label: 'Escapement',
    kind: 'lookup',
    lookupCollection: 'lookup_escapement_type',
  },
  {
    key: 'antiShockSystem',
    label: 'Anti-Shock System',
    kind: 'lookup',
    lookupCollection: 'lookup_shock_protection',
  },
  { key: 'rotorType', label: 'Rotor Type', kind: 'text', placeholder: 'e.g., Ball-bearing' },
  { key: 'jewels', label: 'Jewels', kind: 'number' },
  { key: 'powerReserveHours', label: 'Power Reserve (h)', kind: 'number' },
  { key: 'positionsAdjusted', label: 'Positions Adjusted', kind: 'number' },
  // Phase 3 durability trio (v1.94.0). Numeric REPLACE-when-set overrides —
  // Ball's Amortiser® shock upgrade and soft-iron anti-magnetic tiers are the
  // canonical drivers. `antiMagneticAm` and `antiMagneticGauss` mirror the
  // base's dual-unit storage; the tier editor renders both, mirroring the
  // base editor in CustomCalibreModal (see labels at ~line 472).
  { key: 'shockResistanceG', label: 'Shock Resistance (G)', kind: 'number' },
  { key: 'antiMagneticAm', label: 'Anti-Magnetic (A/m)', kind: 'number' },
  { key: 'antiMagneticGauss', label: 'Anti-Magnetic (Gauss)', kind: 'number' },
  // The three accuracy keys form ONE logical spec (see formatAccuracy). They're
  // in the whitelist so the generic sanitize/count/apply helpers handle them,
  // but the editors render them as a single grouped control via
  // ACCURACY_OVERRIDE_KEYS, not three independent rows.
  { key: 'accuracySymmetric', label: 'Accuracy Symmetric', kind: 'boolean' },
  { key: 'accuracyLowerSecPerDay', label: 'Accuracy Lower (s/day)', kind: 'number' },
  { key: 'accuracyUpperSecPerDay', label: 'Accuracy Upper (s/day)', kind: 'number' },
];

/**
 * Compile-time exhaustiveness guard over {@link OverrideKey}. Every key of
 * `CalibreTierOverrides` must appear here, so adding a field to that interface
 * without deciding how it is EDITED fails the build rather than shipping a
 * silently uneditable override.
 *
 * This exists because the override whitelist is duplicated across three places
 * — this file, the local `CalibreTierOverrides` in services/firebase.ts, and
 * the sanitiser key lists in functions/src/aiEnrich.ts — and two of them had
 * already drifted. The paired runtime test asserts OVERRIDABLE_FIELDS covers
 * exactly these keys; the functions-side list stays a manual mirror (separate
 * tsconfig, so no compile-time link can span the boundary).
 */
export const ALL_OVERRIDE_KEYS: Record<OverrideKey, true> = {
  isChronometer: true,
  isCosc: true,
  isGlashuetteRegulation: true,
  isGenevaSeal: true,
  isGrandSeikoStandard: true,
  isMETAS: true,
  regulatorSystem: true,
  hairspring: true,
  rotorType: true,
  escapement: true,
  antiShockSystem: true,
  hasDate: true,
  balanceType: true,
  decorations: true,
  rotorMaterial: true,
  bridgeMaterial: true,
  plateFinishing: true,
  caseMaterialsAvailable: true,
  jewels: true,
  powerReserveHours: true,
  positionsAdjusted: true,
  accuracyLowerSecPerDay: true,
  accuracyUpperSecPerDay: true,
  accuracySymmetric: true,
  hacking: true,
  quickSetDate: true,
  shockResistanceG: true,
  antiMagneticAm: true,
  antiMagneticGauss: true,
};

/**
 * Map of override key → lookup collection for the lookup-backed overridable
 * fields (regulatorSystem, hairspring, escapement, antiShockSystem, the
 * v1.92.0 materials trio rotorMaterial / bridgeMaterial / plateFinishing, and
 * the multi-select balanceType / decorations / caseMaterialsAvailable). Lets
 * the effective-spec previews resolve a stored id (e.g. `kif`) to its human
 * label.
 */
export const OVERRIDE_LOOKUP_COLLECTIONS: Partial<Record<OverrideKey, string>> =
  Object.fromEntries(
    OVERRIDABLE_FIELDS.filter((f) => f.lookupCollection).map((f) => [
      f.key,
      f.lookupCollection as string,
    ]),
  );

/**
 * The override keys that together form the accuracy spec. They're rendered as
 * ONE grouped control (base editor + tier editor) and ONE effective-spec row
 * (ref editor) rather than three independent fields — the two numeric bounds
 * are reinterpreted by the `accuracySymmetric` flag (see {@link formatAccuracy}).
 */
export const ACCURACY_OVERRIDE_KEYS: readonly OverrideKey[] = [
  'accuracySymmetric',
  'accuracyLowerSecPerDay',
  'accuracyUpperSecPerDay',
];

const ACCURACY_KEY_SET = new Set<OverrideKey>(ACCURACY_OVERRIDE_KEYS);

/** True for the three accuracy keys, which get grouped/special rendering. */
export function isAccuracyKey(key: OverrideKey): boolean {
  return ACCURACY_KEY_SET.has(key);
}

/**
 * Render the accuracy spec per the semantic contract shared with
 * @watchkeeper/shared-types (v1.33.0). Reused everywhere accuracy is displayed
 * so the two surfaces (base editor preview, tier editor preview, ref-editor
 * effective spec) never drift.
 *
 * - `symmetric` false/unset → SIGNED asymmetric bounds:
 *     lower=-4, upper=+6  → "-4/+6 sec/day"
 * - `symmetric` true → symmetric spread spec (both unsigned, lower <= upper):
 *     lower=20, upper=20  → "±20 sec/day"  (collapses when equal)
 *     lower=10, upper=12  → "±10 to ±12 sec/day"
 *
 * Either bound may be undefined; `—` when both are.
 */
export function formatAccuracy(
  symmetric: boolean | undefined,
  lower: number | undefined,
  upper: number | undefined,
): string {
  if (lower === undefined && upper === undefined) return '—';
  if (symmetric) {
    const lo = lower ?? upper;
    const hi = upper ?? lower;
    // Both defined here (at least one is non-undefined; the ?? fills the other).
    if (lo === undefined || hi === undefined) return '—';
    if (Math.abs(lo) === Math.abs(hi)) return `±${Math.abs(lo)} sec/day`;
    return `±${Math.abs(lo)} to ±${Math.abs(hi)} sec/day`;
  }
  const signed = (n: number) => (n >= 0 ? `+${n}` : `${n}`);
  if (lower !== undefined && upper !== undefined) {
    return `${signed(lower)}/${signed(upper)} sec/day`;
  }
  const only = (lower ?? upper) as number;
  return `${signed(only)} sec/day`;
}

/**
 * Sanitize a tier for a Firestore write. Tiers are persisted inside
 * `CalibreData.tiers` and the calibre save path only strips `undefined` values
 * SHALLOWLY (it never recurses into array elements) — so any `undefined` on a
 * tier or inside its `overrides` reaches Firestore and gets rejected with
 * "Unsupported field value: undefined". This drops undefined keys at both
 * levels: cleared tier fields (e.g. an empty `description`) and unset override
 * fields become simply absent. Returns a fresh tier — never mutates the input.
 *
 * The rebuilt tier must carry EVERY persisted field: this is the only write
 * boundary for the calibre editor, so anything omitted here is silently erased
 * on save. `tierType`/`brandId` were missing until now, which stripped the
 * scoping metadata off brand-specific tiers created by the reclassify wizard
 * (which writes via `appendCalibreTier` and so bypasses this function) the
 * moment the calibre was next saved from the editor.
 */
export function sanitizeTierForWrite(tier: CalibreTier): CalibreTier {
  const overrides = Object.fromEntries(
    Object.entries(tier.overrides).filter(([, v]) => v !== undefined),
  ) as CalibreTierOverrides;
  const clean: CalibreTier = {
    id: tier.id,
    name: tier.name,
    // Absent reads as 'general' per the CalibreTier contract — stamp it
    // explicitly so tiers predating the field normalize on their next save.
    tierType: tier.tierType ?? 'general',
    overrides,
  };
  if (tier.description !== undefined) clean.description = tier.description;
  // The brand only travels with a brand-specific tier: a general grade belongs
  // to the movement manufacturer, not a brand, and the type requires brandId to
  // be absent there. Enforced here because the type layer can't express it.
  const brandId = tier.brandId?.trim();
  if (clean.tierType === 'brand-specific' && brandId) clean.brandId = brandId;
  // The tier's own manufacturers and images (shared-types v1.136.0). Passed
  // through — this sanitizer is an ALLOW-list, so a field not named here is
  // silently dropped by every tier save, not just the reclassify wizard's.
  const manufacturerId = tier.manufacturerId?.trim();
  if (manufacturerId) clean.manufacturerId = manufacturerId;
  const additional = [
    ...new Set((tier.additionalManufacturerIds ?? []).map((id) => id?.trim()).filter(Boolean)),
  ].filter((id) => id !== manufacturerId);
  if (additional.length > 0) clean.additionalManufacturerIds = additional;
  const images = (tier.images ?? [])
    .filter((img) => !!(img?.storagePath || img?.url))
    .map(
      (img) =>
        Object.fromEntries(Object.entries(img).filter(([, v]) => v !== undefined)) as CalibreImage,
    );
  if (images.length > 0) clean.images = images;
  return clean;
}

/** The manufacturers a reclassified calibre hands to its tier. */
export interface TierManufacturerCarryOver {
  manufacturerId?: string;
  additionalManufacturerIds?: string[];
}

type ManufacturerSource = {
  manufacturerId?: string | null;
  additionalManufacturerIds?: string[] | null;
};

function manufacturerSet(m: ManufacturerSource): TierManufacturerCarryOver {
  const primary = m.manufacturerId?.trim() || undefined;
  const additional = [
    ...new Set((m.additionalManufacturerIds ?? []).map((id) => id?.trim()).filter(Boolean)),
  ].filter((id) => id !== primary) as string[];
  return {
    ...(primary ? { manufacturerId: primary } : {}),
    ...(additional.length > 0 ? { additionalManufacturerIds: additional } : {}),
  };
}

/**
 * The source calibre's manufacturers, primary + additional, as its tier
 * carries them (Towson's Benzinger-decorated Valjoux: valjoux + [benzinger]).
 * Null/blank ids are dropped and the primary is never repeated in the list.
 *
 * Nothing when the source's set is the same as the TARGET's (a Ball calibre
 * reclassified into a Ball base): a tier's absent manufacturers mean "the base
 * calibre's", so repeating them would add nothing — and would turn "reuse an
 * existing tier untouched" into a write.
 */
export function tierManufacturersFromSource(
  source: ManufacturerSource,
  target?: ManufacturerSource | null,
): TierManufacturerCarryOver {
  const fromSource = manufacturerSet(source);
  if (target) {
    const base = manufacturerSet(target);
    const same =
      fromSource.manufacturerId === base.manufacturerId &&
      [...(fromSource.additionalManufacturerIds ?? [])].sort().join('|') ===
        [...(base.additionalManufacturerIds ?? [])].sort().join('|');
    if (same) return {};
  }
  return fromSource;
}

/**
 * Every image a source calibre carries — entry-level `images` and the
 * mirrored `data.images` — once each (by storage path, else URL).
 */
export function sourceCalibreImages(source: {
  images?: CalibreImage[] | null;
  data?: { images?: CalibreImage[] | null } | null;
}): CalibreImage[] {
  const seen = new Set<string>();
  const out: CalibreImage[] = [];
  for (const img of [...(source.images ?? []), ...(source.data?.images ?? [])]) {
    const key = img?.storagePath || img?.url;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(img);
  }
  return out;
}

/**
 * Fold a reclassified calibre's manufacturers and (already copied) images
 * into the tier the migration points at:
 *  - a NEW tier takes them as they are;
 *  - an EXISTING tier keeps a primary manufacturer it already has (fill-empty,
 *    like every bulk write) — the source's primary then joins its additional
 *    list, so nothing is lost — gains any additional manufacturers it lacks,
 *    and has the copied images appended.
 */
export function applyTierCarryOver(
  tier: CalibreTier,
  manufacturers: TierManufacturerCarryOver,
  copiedImages: CalibreImage[],
): CalibreTier {
  const manufacturerId = tier.manufacturerId?.trim() || manufacturers.manufacturerId;
  // Nothing the source had is lost: if the tier keeps a different primary of
  // its own, the source's primary joins the additional list.
  const additional = [
    ...new Set([
      ...(tier.additionalManufacturerIds ?? []),
      ...(manufacturers.manufacturerId ? [manufacturers.manufacturerId] : []),
      ...(manufacturers.additionalManufacturerIds ?? []),
    ]),
  ].filter((id) => id && id !== manufacturerId);
  return {
    ...tier,
    ...(manufacturerId ? { manufacturerId } : {}),
    ...(additional.length > 0 ? { additionalManufacturerIds: additional } : {}),
    ...(copiedImages.length > 0 || tier.images?.length
      ? { images: [...(tier.images ?? []), ...copiedImages] }
      : {}),
  };
}

/**
 * A copy of `source` ready to be saved as a NEW tier: same scope, same
 * overrides, fresh id, no name.
 *
 * The name is deliberately blank — the curator names the copy, and a tier whose
 * name duplicates another is exactly the ambiguity `resolveTierSuggestion` and
 * the family vocabulary exist to prevent. The description is dropped for the
 * same reason: it describes the tier it was written for.
 *
 * Multi-value overrides hold ARRAYS, so each is copied rather than shared —
 * otherwise editing the copy's case materials would silently rewrite the
 * original's, and the two tiers only diverge at save time.
 *
 * The motivating case: a brand's "Gold Rotor" tier differs from its "MM" tier by
 * one override out of six or more. Re-entering the other five by hand is where
 * the mistakes come from.
 */
export function cloneTier(source: CalibreTier, newId: string): CalibreTier {
  const overrides = Object.fromEntries(
    Object.entries(source.overrides)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, Array.isArray(v) ? [...v] : v]),
  ) as CalibreTierOverrides;

  const clone: CalibreTier = {
    id: newId,
    name: '',
    tierType: source.tierType ?? 'general',
    overrides,
  };
  // Scope travels with the copy: cloning a brand-specific tier that quietly
  // became general would widen it to every brand on a shared movement.
  if (clone.tierType === 'brand-specific' && source.brandId?.trim()) {
    clone.brandId = source.brandId.trim();
  }
  // Manufacturers and images travel too. The images share storage paths with
  // the source, which is safe: removing a tier image never deletes its bytes.
  if (source.manufacturerId?.trim()) clone.manufacturerId = source.manufacturerId.trim();
  if (source.additionalManufacturerIds?.length) {
    clone.additionalManufacturerIds = [...source.additionalManufacturerIds];
  }
  if (source.images?.length) clone.images = source.images.map((img) => ({ ...img }));
  return clone;
}

/**
 * The tiers of one calibre that are legitimately available to ONE brand's
 * references: every general (manufacturer) grade, plus that brand's own
 * brand-specific modifications — never another brand's.
 *
 * A shared base movement accumulates brand-specific tiers from every brand that
 * modifies it (a Sellita SW200/SW510 carries a tier for each brand that adjusts
 * it). Handing the AI the whole `tiers[]` let it pick a tier belonging to a
 * completely different brand off a loose textual cue — e.g. a page describing a
 * calibre as the "manual version of SW510" matched a foreign brand's SW510 tier
 * by the reference stem. So the enrichment surfaces scope the list here BEFORE
 * it reaches the prompt, and resolve the model's answer against the same scoped
 * list so a name match can't reach a foreign tier either.
 *
 * Scoping rules, matching the `CalibreTier` contract:
 *  - `tierType: 'general'` (and absent/undefined, which reads as general for
 *    back-compat) — universal, always included.
 *  - `tierType: 'brand-specific'` — included only when its `brandId` equals the
 *    target brand.
 *  - a brand-specific tier with no `brandId` is malformed (the type requires one)
 *    and is EXCLUDED: it can't be shown to belong to this brand, and the whole
 *    point here is to not offer tiers we can't attribute.
 *
 * With no target brand, only the universal tiers survive.
 */
export function tiersForBrand(
  tiers: CalibreTier[] | undefined,
  brandId: string | undefined,
): CalibreTier[] {
  if (!tiers || tiers.length === 0) return [];
  const target = brandId?.trim();
  return tiers.filter((t) => {
    if (!t) return false;
    if (t.tierType !== 'brand-specific') return true;
    return Boolean(target) && t.brandId?.trim() === target;
  });
}

/**
 * Options for a human-facing tier dropdown on a reference editor: the
 * brand-scoped list from {@link tiersForBrand} plus — ONLY when the reference
 * already pins a tier belonging to another brand — that foreign tier prepended
 * so the <select>'s value stays valid and the curator can see what the ref
 * points at.
 *
 * This is the picker boundary, not an auto-cleaner: a foreign pointer is an
 * existing fact to preserve, not an orphan to clear. The caller renders it
 * with its owning brand called out ("Foo — Rolex mod (other brand)") so the
 * stored scope is legible.
 *
 * Call with the FULL tier list and this brand's id — not with the already-
 * scoped list — because the whole point is to detect a foreign current pick.
 */
export function tierPickerOptions(
  tiers: CalibreTier[] | undefined,
  brandId: string | undefined,
  currentTierId: string | undefined,
): CalibreTier[] {
  const scoped = tiersForBrand(tiers, brandId);
  const current = currentTierId?.trim();
  if (!current) return scoped;
  const selected = (tiers ?? []).find((t) => t && t.id === current);
  if (!selected) return scoped;
  if (scoped.some((t) => t.id === selected.id)) return scoped;
  return [selected, ...scoped];
}

/** Number of override fields a tier actually sets (non-undefined). */
export function countOverrides(tier: CalibreTier): number {
  return OVERRIDABLE_FIELDS.reduce(
    (n, f) => (tier.overrides[f.key] !== undefined ? n + 1 : n),
    0,
  );
}

/**
 * The base value of an overridable field, for display in the override picker /
 * effective-spec preview. Reads straight off `CalibreData` (the override keys
 * are a strict subset of CalibreData keys).
 */
export function baseValueFor(
  base: CalibreData | undefined,
  key: OverrideKey,
): OverrideValue {
  if (!base) return undefined;
  // `hasDate` is the one override key with no direct CalibreData counterpart:
  // the base carries the TYPED `date[]` array, the tier only a presence flag.
  // Derive the base side from that array so the "Inherit → base is Yes/No"
  // subtext tells the truth. An absent array reads as "no date", matching how
  // the effective spec renders it.
  if (key === 'hasDate') return (base.date?.length ?? 0) > 0;
  return base[key as Exclude<OverrideKey, 'hasDate'>];
}

/** One resolved field of the effective (base + tier) spec. */
export interface EffectiveField {
  key: OverrideKey;
  label: string;
  kind: OverrideKind;
  /** The value after applying the tier override (falls back to base). */
  value: OverrideValue;
  /** True when the tier explicitly overrides this field. */
  overridden: boolean;
}

/**
 * Resolve the effective spec for the overridable fields: tier override wins
 * when set (non-undefined), otherwise the base value falls through. Returns one
 * entry per overridable field, flagging which came from the tier — the ref
 * editor uses `overridden` to badge tier-sourced values.
 */
export function applyTierOverrides(
  base: CalibreData | undefined,
  tier: CalibreTier | undefined,
): EffectiveField[] {
  return OVERRIDABLE_FIELDS.map((f) => {
    const override = tier?.overrides[f.key];
    const overridden = override !== undefined;
    return {
      key: f.key,
      label: f.label,
      kind: f.kind,
      value: overridden ? override : baseValueFor(base, f.key),
      overridden,
    };
  });
}

/**
 * Render an overridable value as a short display string. `—` when unset.
 *
 * An array renders as its comma-joined members — raw slugs, since this helper
 * has no lookup context. Callers holding a lookup collection for the key should
 * resolve each member to its label first (see `displayOverrideLookupValue`).
 * An EMPTY array is meaningful for `decorations` (it strips the base list
 * wholesale), so it renders as "None" rather than the unset `—`.
 */
export function displayOverrideValue(value: OverrideValue): string {
  if (value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length === 0 ? 'None' : value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

/**
 * Render an overridable value, resolving lookup slugs to their human labels
 * when the key is lookup-backed. Handles both the single-slug (`lookup`) and
 * multi-slug (`multi-lookup`) kinds — the array case resolves each member, so a
 * tier's decorations read "Perlage, Côtes de Genève" rather than raw slugs.
 *
 * Shared by every effective-spec surface (tier editor, ref editor, per-ref
 * override editor) so slug rendering can't drift between them.
 */
export function displayOverrideLookupValue(
  key: OverrideKey,
  value: OverrideValue,
  resolveLookupLabel: (collection: string, raw: string) => string,
): string {
  const collection = OVERRIDE_LOOKUP_COLLECTIONS[key];
  if (!collection) return displayOverrideValue(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return 'None';
    return value.map((slug) => resolveLookupLabel(collection, slug)).join(', ');
  }
  if (typeof value === 'string' && value !== '') {
    return resolveLookupLabel(collection, value);
  }
  return displayOverrideValue(value);
}

/**
 * Case- and accent-insensitive normalization: NFD-decompose, strip combining
 * diacritics, lowercase. "Élaboré" → "elabore", "Chronomètre" → "chronometre".
 */
export function normalizeForTierMatch(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** True when `term` appears as a whole word (ASCII word boundaries) in `haystack`. */
function wordBoundaryMatch(haystack: string, term: string): boolean {
  if (!term) return false;
  return new RegExp(`\\b${escapeRegExp(term)}\\b`).test(haystack);
}

/**
 * Common tier-name misspellings / abbreviations, grouped. A tier pulls in a
 * whole group when its normalized name equals ANY member of that group — so a
 * calibre whose tier is spelled "Chronomètre" (normalizes to "chronometre")
 * still matches source strings that only say "chrono" or "cosc", and vice
 * versa. Kept small and explicit; extend as new variants are discovered.
 */
const TIER_ALIAS_GROUPS: readonly (readonly string[])[] = [
  ['elabore', 'elabora', 'elab'],
  ['chronometer', 'chronometre', 'chrono', 'cosc'],
  ['standard', 'std'],
  ['top'], // single-word, high false-positive risk — word-boundary only, no substring
];

/**
 * Substring fallback is only safe for reasonably long, distinctive tier names.
 * Short single-word tiers ("Top", "Std") must match on a word boundary only, or
 * they'd fire inside unrelated tokens ("topper", "standardize").
 */
const MIN_SUBSTRING_TIER_LEN = 6;

/**
 * Every normalized spelling that should be treated as naming `name`.
 *
 * The tier's own normalized name comes first, followed by the rest of whichever
 * {@link TIER_ALIAS_GROUPS} entry it belongs to (order within the group, minus
 * the name itself). A name in no group yields just itself, so callers can
 * always iterate the result rather than special-casing "has aliases".
 *
 * Exported because there are TWO tier detectors and they must not disagree
 * about what "COSC" means. `detectTierFromCalibreString` searches a raw string
 * for a tier; `detectTierSuffixCandidates` (calibreTierSuffix.ts) splits an
 * unmatched calibre string into base + tier. The second one compared spellings
 * by equality and so read "Sellita SW200-1 COSC" as having no tier at all,
 * while the first would happily have resolved the same word. One table, one
 * answer.
 *
 * The comparison this feeds is equality between two normalized terms — it does
 * NOT imply the substring/word-boundary levels that
 * `detectTierFromCalibreString` layers on top for free-text search.
 */
export function tierMatchTerms(name: string): string[] {
  const normName = normalizeForTierMatch(name.trim());
  if (!normName) return [];
  const group = TIER_ALIAS_GROUPS.find((g) => g.includes(normName));
  if (!group) return [normName];
  return [normName, ...group.filter((a) => a !== normName)];
}

/**
 * True when `a` and `b` name the same tier grade, allowing for accents, case
 * and the alias table — `matchesTierName('COSC', 'Chronomètre')` is true.
 *
 * Both sides are expanded, so it holds whether the catalogue spells the tier
 * "Chronometer" and the scrape says "COSC" or the other way round.
 */
export function matchesTierName(a: string, b: string): boolean {
  const termsA = tierMatchTerms(a);
  if (termsA.length === 0) return false;
  const termsB = new Set(tierMatchTerms(b));
  return termsA.some((t) => termsB.has(t));
}

/**
 * Detect a calibre tier from a raw calibre/movement string by matching the
 * calibre's own `tiers[]` names (and a small alias table) against it. Used at
 * bulk-import time to auto-populate `WatchReference.calibreTierId` when the
 * scraped calibre string carries a tier signal (e.g. "Sellita SW200 (Elaboré)").
 *
 * Matching is case- and accent-insensitive. Priority, highest first:
 *   1. tier name as a whole word         ("... (Elaboré)" → "Elaboré")
 *   2. an alias of the tier as a whole word ("... Chronomètre" → "Chronometer")
 *   3. tier name as a plain substring     (only for names ≥ 6 chars)
 * The first tier that matches at the highest attainable level wins. Returns
 * `null` when nothing matches — the safe default that leaves the tier unset.
 *
 * Test expectations (no test runner is wired up in this repo yet):
 *   tiers = [Base, Elaboré, Top, Chronometer, Standard]
 *   "Sellita SW200 (Elaboré)"      → Elaboré      (word-boundary on name)
 *   "Sellita SW200-1 Elabore"      → Elaboré      (accent-insensitive)
 *   "ETA 2892 Chronomètre"         → Chronometer  (alias: "chronometre")
 *   "ETA 2892-A2"                  → null
 *   "Ronda 715 Standard"           → Standard
 *   "Miyota 9015 Top"              → Top          (word-boundary; NOT "9015topper")
 *   "Sellita SW200 no known tier"  → null
 */
export function detectTierFromCalibreString(
  calibreString: string,
  tiers: CalibreTier[],
): { tierId: string; matchedText: string } | null {
  if (!calibreString || !tiers || tiers.length === 0) return null;
  const haystack = normalizeForTierMatch(calibreString);

  // Precompute each tier's normalized name and its full set of search terms
  // (name + any alias group it belongs to).
  const candidates = tiers
    .filter((t) => t.name && t.name.trim())
    .map((tier) => {
      // `tierMatchTerms` returns the name first, then its aliases — and the name
      // is handled at the higher-priority level below, so drop the head.
      const [normName, ...aliases] = tierMatchTerms(tier.name);
      return { tier, normName, aliases };
    });

  // Level 1: tier name as a whole word.
  for (const c of candidates) {
    if (wordBoundaryMatch(haystack, c.normName)) {
      return { tierId: c.tier.id, matchedText: c.normName };
    }
  }
  // Level 2: an alias as a whole word.
  for (const c of candidates) {
    for (const alias of c.aliases) {
      if (wordBoundaryMatch(haystack, alias)) {
        return { tierId: c.tier.id, matchedText: alias };
      }
    }
  }
  // Level 3: tier name as a plain substring (long, distinctive names only).
  for (const c of candidates) {
    if (c.normName.length >= MIN_SUBSTRING_TIER_LEN && haystack.includes(c.normName)) {
      return { tierId: c.tier.id, matchedText: c.normName };
    }
  }
  return null;
}

/**
 * Resolve an AI-suggested calibre tier (a stable id and/or a human-readable
 * name) against a SPECIFIC calibre's `tiers[]`. Used by the ref-editor AI
 * apply-flow, where two things go wrong with a naive exact-match:
 *
 *   1. The AI may paraphrase the tier name — return "Elaboré" for a tier that
 *      is actually named "Special (Elaboré)".
 *   2. When the AI also changes the calibre, the suggested `calibreTierId`
 *      belongs to the OLD calibre; on the newly-linked calibre that id doesn't
 *      exist, so resolution must fall through to the name.
 *
 * Resolution order, first hit wins:
 *   1. exact id match                       (only valid when the id belongs here)
 *   2. exact name match (case-insensitive)
 *   3. the suggestion string carries a tier name/alias — e.g. "Sellita SW200
 *      Elaboré", or an alias like "chrono"/"cosc" (via detectTierFromCalibreString)
 *   4. the suggestion appears as a whole word INSIDE a tier name — the reverse
 *      of (3), for paraphrases: "Elaboré" ⊂ "Special (Elaboré)". Accent-
 *      insensitive, and only when EXACTLY ONE tier matches (never guess).
 *
 * Returns the resolved tier id, or `null` when nothing matches (or step 4 is
 * ambiguous). Callers treat `null` as "leave/clear the tier" — never a guess.
 */
export function resolveTierSuggestion(
  tiers: CalibreTier[],
  idSuggestion: string | undefined,
  nameSuggestion: string | undefined,
): string | null {
  if (!tiers || tiers.length === 0) return null;

  // 1. exact id — only meaningful when the id actually belongs to these tiers.
  const id = idSuggestion?.trim();
  if (id) {
    const byId = tiers.find((t) => t.id === id);
    if (byId) return byId.id;
  }

  const name = nameSuggestion?.trim();
  if (!name) return null;

  // 2. exact name (case-insensitive).
  const lower = name.toLowerCase();
  const exact = tiers.find((t) => t.name.trim().toLowerCase() === lower);
  if (exact) return exact.id;

  // 3. the suggestion carries a tier name or alias as a token
  //    ("… Elaboré", "chrono", "cosc"). Reuse the bulk-import detector.
  const forward = detectTierFromCalibreString(name, tiers);
  if (forward) return forward.tierId;

  // 4. reverse of (3): the suggestion is a whole word inside a tier name
  //    ("Elaboré" ⊂ "Special (Elaboré)"). Reuse the SAME accent-insensitive
  //    word-boundary matcher by flipping the arguments — treat each tier's own
  //    name as the haystack and the suggestion as a one-off tier. Require a
  //    unique hit so an ambiguous paraphrase never silently guesses.
  const reverseHits = tiers.filter(
    (t) =>
      detectTierFromCalibreString(t.name, [
        { id: t.id, name, overrides: {} },
      ]) !== null,
  );
  if (reverseHits.length === 1) return reverseHits[0].id;

  return null;
}

/** The effective (base + tier-override) accuracy, formatted and flagged. */
export interface EffectiveAccuracy {
  /** Formatted per {@link formatAccuracy}; `—` when unset. */
  display: string;
  /** True when the tier overrode ANY of the three accuracy keys. */
  overridden: boolean;
}

/**
 * Resolve the effective accuracy spec: each of the three accuracy keys resolves
 * independently (tier override wins when set, else base falls through), then the
 * trio is formatted with {@link formatAccuracy}. `overridden` is true when the
 * tier set any of them — the ref editor badges the accuracy row on that.
 */
export function effectiveAccuracy(
  base: CalibreData | undefined,
  tier: CalibreTier | undefined,
): EffectiveAccuracy {
  function resolve<T>(key: OverrideKey): { value: T | undefined; overridden: boolean } {
    const o = tier?.overrides[key];
    if (o !== undefined) return { value: o as T, overridden: true };
    return { value: baseValueFor(base, key) as T | undefined, overridden: false };
  }
  const sym = resolve<boolean>('accuracySymmetric');
  const lo = resolve<number>('accuracyLowerSecPerDay');
  const hi = resolve<number>('accuracyUpperSecPerDay');
  return {
    display: formatAccuracy(sym.value, lo.value, hi.value),
    overridden: sym.overridden || lo.overridden || hi.overridden,
  };
}

/* ------------------------------------------------------------------------ *
 * AI-apply tier merge: field enumeration
 * ------------------------------------------------------------------------ */

/**
 * A field the AI-apply merge flow can overlay onto an existing tier: any
 * override key, the tier's own `description` (which is NOT an override — it
 * lives on the tier object), or the grouped `accuracy` pseudo-field standing in
 * for the three accuracy keys.
 *
 * `description` is safe as a plain literal: it is not a member of
 * {@link OverrideKey}, so it can never collide with a real override key.
 */
export type MergeableTierKey = OverrideKey | 'description' | 'accuracy';

/** One row of the merge field-picker: what it writes and how it reads today. */
export interface MergeableTierField {
  key: MergeableTierKey;
  label: string;
  /**
   * The override keys this row writes. Empty for `description`, which targets
   * the tier field instead; three-strong for the grouped `accuracy` row.
   */
  overrideKeys: OverrideKey[];
  /** The existing tier's value, display-formatted; `—` when unset. */
  existing: string;
  /**
   * The value the tier would read if this row is ticked, display-formatted.
   *
   * For every row but `accuracy` this is simply the proposed value, because a
   * single-key overwrite IS the result. The `accuracy` row resolves the trio
   * (proposed key where offered, existing key otherwise) so a PARTIAL accuracy
   * proposal doesn't advertise a spec the merge won't actually produce.
   */
  proposed: string;
}

/**
 * True for a value that carries no information: unset, null, empty string, or
 * empty array.
 *
 * Deliberately STRICTER about `[]` than the `v !== '' && v != null` filters on
 * the fill-empty remap path. An empty array is meaningful in the tier data
 * model (an empty `decorations` strips the base list wholesale), but an AI
 * returning `[]` far more often means "found nothing" than "definitively none"
 * — and this merge path OVERWRITES a curated value, so the cost of reading it
 * wrong is higher than on the fill-empty path. Ambiguous, so not offered.
 *
 * `false` and `0` are real values and are NOT empty.
 */
export function isEmptyOverrideValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Enumerate the fields an AI-proposed tier could overlay onto an existing tier,
 * one row per user-tickable decision, each carrying both sides of the diff.
 *
 * Only fields the proposal actually carries a NON-EMPTY value for are returned:
 * a null/absent value is the model saying "I don't know", never "clear this"
 * (clearing a curated value stays a job for the tier editor). Unknown keys —
 * AI drift past the whitelist — are dropped by construction, since the walk is
 * over {@link OVERRIDABLE_FIELDS} rather than over the proposal's own keys.
 *
 * Lookup-backed values render as their raw slugs: this is the same rendering
 * the proposed-tier row and the tier editor already use, neither of which has
 * a lookup-label resolver in scope.
 */
export function mergeableTierFields(
  target: CalibreTier,
  proposed: { description?: string; overrides?: CalibreTierOverrides },
): MergeableTierField[] {
  const overrides = proposed.overrides ?? {};
  const rows: MergeableTierField[] = [];

  const description = proposed.description?.trim();
  if (description) {
    rows.push({
      key: 'description',
      label: 'Description',
      overrideKeys: [],
      existing: target.description?.trim() || '—',
      proposed: description,
    });
  }

  for (const field of OVERRIDABLE_FIELDS) {
    if (isAccuracyKey(field.key)) continue; // grouped below
    const value = overrides[field.key];
    if (isEmptyOverrideValue(value)) continue;
    rows.push({
      key: field.key,
      label: field.label,
      overrideKeys: [field.key],
      existing: displayOverrideValue(target.overrides[field.key]),
      proposed: displayOverrideValue(value),
    });
  }

  // The accuracy trio is ONE decision. Ticking a single bound onto a tier whose
  // other two keys disagree yields an incoherent spec (a symmetric flag against
  // signed bounds), so the row writes every accuracy key the proposal offers,
  // all or nothing.
  const offeredAccuracyKeys = ACCURACY_OVERRIDE_KEYS.filter(
    (k) => !isEmptyOverrideValue(overrides[k]),
  );
  if (offeredAccuracyKeys.length > 0) {
    const resolve = <T>(key: OverrideKey): T | undefined => {
      const value = offeredAccuracyKeys.includes(key)
        ? overrides[key]
        : target.overrides[key];
      return value as T | undefined;
    };
    rows.push({
      key: 'accuracy',
      label: 'Accuracy',
      overrideKeys: [...offeredAccuracyKeys],
      existing: formatAccuracy(
        target.overrides.accuracySymmetric,
        target.overrides.accuracyLowerSecPerDay,
        target.overrides.accuracyUpperSecPerDay,
      ),
      proposed: formatAccuracy(
        resolve<boolean>('accuracySymmetric'),
        resolve<number>('accuracyLowerSecPerDay'),
        resolve<number>('accuracyUpperSecPerDay'),
      ),
    });
  }

  // Drop rows where the tier already reads exactly that way. They are inert —
  // ticking one writes nothing (planProposedTierMerge drops them again on the
  // way to the document) — and an offered-but-inert row makes the picker's
  // "n fields will overwrite" count lie. Compared on the DISPLAY strings, which
  // is what the row actually asserts, and which is the only sound comparison
  // for the resolved `accuracy` row.
  return rows.filter((row) => row.existing !== row.proposed);
}
